/**
 * AllFrameworksData.js
 * Master Consolidated Hub & Single Source of Truth for all Compliance Frameworks & Standards
 * 
 * Location: e:\CARAgrc\cara_frontend\src\components\ComplianceOperations\AllFrameworksData.js
 * 
 * Contains:
 * - All Framework Registries & Metadata
 * - All Industry Pre-Fill Mappings
 * - Centralized API Data Loader Service Methods
 * - React Hooks (useFrameworkData, useNISTCSF2Data)
 * - Radar Chart Visualization Utilities
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import i18n from '../../i18n';
import secureDataService from '../../services/SecureDataService';

const CISV8_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/';

// ============================================================================
// 1. ALL FRAMEWORKS REGISTRY & METADATA
// ============================================================================
export const FRAMEWORKS_REGISTRY = {
  nist_csf_2_0: {
    key: 'nist_csf_2_0',
    name: 'NIST Cybersecurity Framework 2.0',
    shortName: 'NIST CSF 2.0',
    version: '2.0',
    description: 'Comprehensive risk management framework featuring 6 core functions: Govern, Identify, Protect, Detect, Respond, Recover.',
    categoryCode: 'NIST2',
    color: '#00D2FF',
    route: '/view-nist-csf2-0',
    backendKey: 'nist_csf_2_0',
    functions: ['GOVERN', 'IDENTIFY', 'PROTECT', 'DETECT', 'RESPOND', 'RECOVER'],
  },
  nist_csf1: {
    key: 'nist_csf1',
    name: 'NIST Cybersecurity Framework 1.1',
    shortName: 'NIST CSF 1.1',
    version: '1.1',
    description: 'Traditional 5-function cybersecurity framework for improving critical infrastructure cybersecurity.',
    categoryCode: 'NIST1',
    color: '#3B82F6',
    route: '/view-nist-csf-1-1',
    backendKey: 'nist_csf1',
    functions: ['IDENTIFY', 'PROTECT', 'DETECT', 'RESPOND', 'RECOVER'],
  },
  iso27001: {
    key: 'iso27001',
    name: 'ISO/IEC 27001:2022 Security Controls',
    shortName: 'ISO 27001',
    version: '2022',
    description: 'International standard for managing information security risks with Annex A control clauses.',
    categoryCode: 'ISO',
    color: '#10B981',
    route: '/view-iso-cara',
    backendKey: 'iso27001',
    functions: ['Organizational Controls', 'People Controls', 'Physical Controls', 'Technological Controls'],
  },
  cis_v8: {
    key: 'cis_v8',
    name: 'CIS Critical Security Controls v8.1',
    shortName: 'CIS v8.1',
    version: '8.1',
    description: 'Prioritized set of 18 safeguard controls to mitigate current cyber attacks.',
    categoryCode: 'CIS',
    color: '#8B5CF6',
    route: '/view-cis-v8-1',
    backendKey: 'cis_v8',
    functions: ['Basic Safeguards', 'Foundational Safeguards', 'Organizational Safeguards'],
  },
  nist_ai_rmf: {
    key: 'nist_ai_rmf',
    name: 'NIST Artificial Intelligence Risk Management Framework',
    shortName: 'NIST AI RMF',
    version: '1.0',
    description: 'Framework to manage risks associated with artificial intelligence systems across 4 functions: Govern, Map, Measure, Manage.',
    categoryCode: 'AI_RMF',
    color: '#EC4899',
    route: '/view-nist-ai-rmf',
    backendKey: 'nist_ai_rmf',
    functions: ['GOVERN', 'MAP', 'MEASURE', 'MANAGE'],
  },
  ransomware: {
    key: 'ransomware',
    name: 'NIST Ransomware Risk Management Profile',
    shortName: 'NIST Ransomware Profile',
    version: '1.0',
    description: 'Specialized profile designed to prevent, detect, respond to, and recover from ransomware threats.',
    categoryCode: 'RANSOMWARE',
    color: '#EF4444',
    route: '/view-ransomware-framework',
    backendKey: 'ransomware',
    functions: ['Identify', 'Protect', 'Detect', 'Respond', 'Recover'],
  },
  rbi_cps: {
    key: 'rbi_cps',
    name: 'RBI Cyber Security Framework for Banks & NBFCs',
    shortName: 'RBI Cyber Security',
    version: '1.0',
    description: 'Reserve Bank of India cyber security mandates and resilience standards for financial institutions.',
    categoryCode: 'RBI',
    color: '#F59E0B',
    route: '/view-rbi-cara',
    backendKey: 'rbi_cps',
    functions: ['Governance & Strategy', 'Security Operations', 'Network Security', 'Incident Management'],
  },
};

// ============================================================================
// 2. UNIFIED & FRAMEWORK-SPECIFIC INDUSTRY PRE-FILL MAPS
// ============================================================================
export const industryPreFillMapNist = {
  "Banking, Financial Services, and Insurance (BFSI)": { priority: "High", status: "Largely Implemented" },
  "Healthcare & Pharmaceuticals": { priority: "High", status: "Partially Implemented" },
  "Information Technology (IT) & Software": { priority: "High", status: "Partially Implemented" },
  "Manufacturing & Supply Chain": { priority: "Medium", status: "Partially Implemented" },
  "Retail & E-commerce": { priority: "Medium", status: "Partially Implemented" },
  "Energy, Oil & Gas": { priority: "High", status: "Largely Implemented" },
  "Telecom & Media": { priority: "High", status: "Partially Implemented" },
  "Education & EdTech": { priority: "Medium", status: "Partially Implemented" },
  "Transportation & Logistics": { priority: "Medium", status: "Partially Implemented" },
  "Aerospace & Defense": { priority: "High", status: "Largely Implemented" },
  "Legal & Professional Services": { priority: "Medium", status: "Partially Implemented" },
  "Real Estate & Construction": { priority: "Medium", status: "Partially Implemented" },
  "Hospitality & Tourism": { priority: "Medium", status: "Partially Implemented" },
  "Automotive & Mobility": { priority: "Medium", status: "Partially Implemented" },
  "NGOs & Non-Profit Organizations": { priority: "Low", status: "Partially Implemented" },
};

// Target state pre-fill map for NIST CSF 2.0 (admin-defined aspirational values per industry)
export const industryPreFillMapNistTarget = {
  "Banking, Financial Services, and Insurance (BFSI)": { priority: "High", status: "In Practice" },
  "Healthcare & Pharmaceuticals": { priority: "High", status: "Largely Implemented" },
  "Information Technology (IT) & Software": { priority: "High", status: "Largely Implemented" },
  "Manufacturing & Supply Chain": { priority: "Medium", status: "Largely Implemented" },
  "Retail & E-commerce": { priority: "Medium", status: "Largely Implemented" },
  "Energy, Oil & Gas": { priority: "High", status: "In Practice" },
  "Telecom & Media": { priority: "High", status: "Largely Implemented" },
  "Education & EdTech": { priority: "Medium", status: "Largely Implemented" },
  "Transportation & Logistics": { priority: "Medium", status: "Largely Implemented" },
  "Aerospace & Defense": { priority: "High", status: "Largely Implemented" },
  "Legal & Professional Services": { priority: "Medium", status: "In Practice" },
  "Real Estate & Construction": { priority: "Medium", status: "Largely Implemented" },
  "Hospitality & Tourism": { priority: "Medium", status: "Largely Implemented" },
  "Automotive & Mobility": { priority: "Medium", status: "Largely Implemented" },
  "NGOs & Non-Profit Organizations": { priority: "Low", status: "Largely Implemented" },
};

export const industryPreFillMapISO = {
  "Banking, Financial Services, and Insurance (BFSI)": { priority: "High", status: "Largely Implemented" },
  "Healthcare & Pharmaceuticals": { priority: "High", status: "Partially Implemented" },
  "Information Technology (IT) & Software": { priority: "High", status: "Largely Implemented" },
  "Manufacturing & Supply Chain": { priority: "High", status: "Partially Implemented" },
  "Retail & E-commerce": { priority: "High", status: "Partially Implemented" },
  "Energy, Oil & Gas": { priority: "High", status: "Largely Implemented" },
  "Telecom & Media": { priority: "High", status: "Partially Implemented" },
  "Education & EdTech": { priority: "Medium", status: "Partially Implemented" },
  "Transportation & Logistics": { priority: "High", status: "Partially Implemented" },
  "Aerospace & Defense": { priority: "High", status: "Largely Implemented" },
  "Legal & Professional Services": { priority: "Medium", status: "Partially Implemented" },
  "Real Estate & Construction": { priority: "Medium", status: "Not Implemented" },
  "Hospitality & Tourism": { priority: "Medium", status: "Partially Implemented" },
  "Automotive & Mobility": { priority: "High", status: "Partially Implemented" },
  "NGOs & Non-Profit Organizations": { priority: "Medium", status: "Not Implemented" },
};

export const industryPreFillMapRansomware = {
  "Banking, Financial Services, and Insurance (BFSI)": { priority: "High", status: "Largely Implemented" },
  "Healthcare & Pharmaceuticals": { priority: "High", status: "Partially Implemented" },
  "Information Technology (IT) & Software": { priority: "High", status: "Largely Implemented" },
  "Manufacturing & Supply Chain": { priority: "High", status: "Partially Implemented" },
  "Retail & E-commerce": { priority: "High", status: "Partially Implemented" },
  "Energy, Oil & Gas": { priority: "High", status: "Largely Implemented" },
  "Telecom & Media": { priority: "High", status: "Partially Implemented" },
  "Education & EdTech": { priority: "Medium", status: "Partially Implemented" },
  "Transportation & Logistics": { priority: "High", status: "Partially Implemented" },
  "Aerospace & Defense": { priority: "High", status: "Largely Implemented" },
  "Legal & Professional Services": { priority: "Medium", status: "Partially Implemented" },
  "Real Estate & Construction": { priority: "Medium", status: "Not Implemented" },
  "Hospitality & Tourism": { priority: "Medium", status: "Partially Implemented" },
  "Automotive & Mobility": { priority: "High", status: "Partially Implemented" },
  "NGOs & Non-Profit Organizations": { priority: "Medium", status: "Not Implemented" },
};

export const industryPreFillMapCISv8 = {
  "Banking, Financial Services, and Insurance (BFSI)": { status: "Partially Implemented" },
  "Healthcare & Pharmaceuticals": { status: "Partially Implemented" },
  "Information Technology (IT) & Software": { status: "Partially Implemented" },
  "Manufacturing & Supply Chain": { status: "Not Implemented" },
  "Retail & E-commerce": { status: "Not Implemented" },
  "Energy, Oil & Gas": { status: "Not Implemented" },
  "Telecom & Media": { status: "Partially Implemented" },
  "Education & EdTech": { status: "Not Implemented" },
  "Transportation & Logistics": { status: "Not Implemented" },
  "Aerospace & Defense": { status: "Partially Implemented" },
  "Legal & Professional Services": { status: "Not Implemented" },
  "Real Estate & Construction": { status: "Not Implemented" },
  "Hospitality & Tourism": { status: "Not Implemented" },
  "Automotive & Mobility": { status: "Partially Implemented" },
  "NGOs & Non-Profit Organizations": { status: "Not Implemented" },
};

export const industryPreFillMapNistAI = {
  "Banking, Financial Services, and Insurance (BFSI)": { priority: "High", status: "Largely Implemented" },
  "Healthcare & Pharmaceuticals": { priority: "High", status: "Partially Implemented" },
  "Information Technology (IT) & Software": { priority: "High", status: "Partially Implemented" },
  "Manufacturing & Supply Chain": { priority: "Medium", status: "Partially Implemented" },
  "Retail & E-commerce": { priority: "Medium", status: "Partially Implemented" },
  "Energy, Oil & Gas": { priority: "High", status: "Largely Implemented" },
  "Telecom & Media": { priority: "High", status: "Partially Implemented" },
  "Education & EdTech": { priority: "Medium", status: "Partially Implemented" },
  "Transportation & Logistics": { priority: "Medium", status: "Partially Implemented" },
  "Aerospace & Defense": { priority: "High", status: "Largely Implemented" },
  "Legal & Professional Services": { priority: "Medium", status: "Partially Implemented" },
  "Real Estate & Construction": { priority: "Medium", status: "Partially Implemented" },
  "Hospitality & Tourism": { priority: "Medium", status: "Partially Implemented" },
  "Automotive & Mobility": { priority: "Medium", status: "Partially Implemented" },
  "NGOs & Non-Profit Organizations": { priority: "Low", status: "Partially Implemented" },
};

export const industryPreFillMapRBI = {
  "Level 1 Banks (Small Finance Banks, Regional Rural Banks, Cooperative Banks)": { priority: "Medium", status: "Not Implemented" },
  "Level 2 Banks (Mid-size Scheduled Commercial Banks, Payment Banks)": { priority: "High", status: "Partially Implemented" },
  "Level 3 Banks (Large Scheduled Commercial Banks, Foreign Banks in India)": { priority: "High", status: "Fully Implemented" },
  "Level 4 Banks (Top Tier Banks, SBI & Large PSBs, Major Private Banks, Systemically Important Banks)": { priority: "High", status: "Fully Implemented" },
};

// ============================================================================
// 3. RADAR CHART VISUALIZATION UTILITIES & COLOR SCHEMES
// ============================================================================
export const statusColors = {
  4: { bg: 'rgba(179, 230, 255, 0.4)', border: '#b3e6ff', point: '#b3e6ff' }, // In Practice
  3: { bg: 'rgba(153, 255, 153, 0.4)', border: '#99ff99', point: '#99ff99' }, // Fully Implemented
  2: { bg: 'rgba(204, 229, 255, 0.4)', border: '#cce5ff', point: '#ccc6ff' }, // Largely Implemented
  1: { bg: 'rgba(255, 214, 153, 0.4)', border: '#ffd699', point: '#ffd699' }, // Partially Implemented
  0: { bg: 'rgba(224, 224, 224, 0.4)', border: '#e0e0e0', point: '#e0e0e0' }, // Not Implemented
};

export function getStatusColor(value) {
  const rounded = Math.min(4, Math.max(0, Math.round(value)));
  return statusColors[rounded] || statusColors[0];
}

export function getRadarData(labels, data, label = "Progress") {
  const backgroundColors = data.map(val => getStatusColor(val).bg);
  const borderColors = data.map(val => getStatusColor(val).border);
  const pointColors = data.map(val => getStatusColor(val).point);

  return {
    labels,
    datasets: [
      {
        label,
        data,
        backgroundColor: backgroundColors,
        borderColor: borderColors,
        borderWidth: 2,
        pointBackgroundColor: pointColors,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };
}

export const options = (labelCount, title, isCombined = false) => {
  const isDetectOrRecover = title === "Detect (DE)" || title === "Recover (RC)";
  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        beginAtZero: true,
        min: 0,
        max: 4,
        ticks: { stepSize: 1, color: '#444' },
        pointLabels: {
          font: "12px",
          callback: function (label) {
            if (!label || label.trim() === " ") return "";
            if (label.includes("Identity Management")) {
              return "Identity Mgnt,\nAuth & Access Ctrl\n(PR.AA)";
            }
            const wrapLength = isCombined ? 28 : 23;
            const regex = new RegExp(`.{1,${wrapLength}}(\\s|$)`, 'g');
            return label.match(regex) || label;
          },
          color: '#000',
        },
        grid: { circular: true },
        angleLines: { display: true },
        startAngle: 0,
      },
    },
    layout: {
      padding: isDetectOrRecover
        ? { top: 50, bottom: 50, left: 0, right: 0 }
        : { top: 0, bottom: 0, left: 0, right: 0 },
    },
    plugins: {
      legend: { display: false },
      datalabels: isCombined
        ? {
          display: true,
          formatter: (value) => value,
          color: "#000",
          font: { size: 12, weight: "bold" },
          anchor: "end",
          align: "start",
          offset: 10,
          clamp: true,
        }
        : { display: false },
    },
  };
};

export const sharedItems = {
  protect: [
    "Access Control", "Awareness and Training", "Data Security",
    "Information Protection Processes and Procedures", "Maintenance", "Protective Technology",
  ],
  detect: ["Anomalies & Events", "Security Continuous Monitoring", "Technology Detection Processes"],
  respond: ["Response Planning", "Communication", "Analysis", "Mitigation", "Improvement"],
  recover: ["Recovery Planning", "Improvements", "Communication"],
};

export const nist2chartData = [
  { title: "Govern (GV)", labels: ['Organizational Context (GV.OC)', 'Risk Management Strategy (GV.RM)', 'Roles, Responsibilities, and Authorities (GV.RR)', 'Policy (GV.PO)', 'Oversight (GV.OV)', 'Cybersecurity Supply Chain Risk Management (GV.SC)'], data: [] },
  { title: "Identify (ID)", labels: ['Assets Management (ID.AM)', 'Risk Assessment (ID.RA)', 'Improvement (ID.IM)'], data: [] },
  { title: "Protect (PR)", labels: ['Identity Management, Authentication, and Access Control (PR.AA)', 'Awareness and Training (PR.AT)', 'Data Security (PR.DS)', 'Platform Security (PR.PS)', 'Technology Infrastructure Resilience (PR.IR)'], data: [] },
  { title: "Detect (DE)", labels: ['Continuous Monitoring (DE.CM)', 'Adverse Event Analysis (DE.AE)'], data: [] },
  { title: "Respond (RS)", labels: ['Incident Management (RS.MA)', 'Incident Analysis (RS.AN)', 'Incident Response Reporting and Communication (RS.CO)', 'Incident Mitigation (RS.MI)'], data: [] },
  { title: "Recover (RC)", labels: ['Incident Recovery Communication (RC.CO)', 'Incident Recovery Plan Execution (RC.RP)'], data: [] },
];

export async function getnist2ChartDataWithScores(API_URL) {
  const response = await fetch(`${API_URL}api/unified_get_status/?framework_key=nist_csf_2_0`);
  const nistData = await response.json();
  function getScore(label) {
    const cleanLabel = label.split(' (')[0];
    const record = nistData.find(item => item.subcategory === cleanLabel);
    return record?.score ?? 0;
  }
  return nist2chartData.map((category) => {
    const scores = category.labels.map(getScore);
    const avg = scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2) : "0";
    return { ...category, data: scores, averageScore: avg };
  });
}

export function getNist2CombinedRadarData(dataWithScores) {
  const labels = [];
  const data = [];
  if (!Array.isArray(dataWithScores)) return { labels: [], datasets: [] };
  dataWithScores.forEach((category) => {
    category.labels.forEach((label, index) => {
      labels.push(label);
      data.push(category.data[index] ?? 0);
    });
  });
  return {
    labels,
    datasets: [{
      label: "Progress", data, fill: true, backgroundColor: "rgba(54, 162, 235, 0.2)",
      borderColor: "#36A2EB", pointBackgroundColor: "#36A2EB", pointBorderColor: "#fff",
      pointHoverBackgroundColor: "#fff", pointHoverBorderColor: "#36A2EB", tension: 0.3,
    }],
  };
}

export const sectionsNist2WithHr = ['GOVERN', 'IDENTIFY', 'PROTECT', 'RESPOND'];

export const sectionDataNist2 = [
  { title: "GOVERN", className: "bg-govern", key: "Govern", items: ["Organizational Context", "Risk Management Strategy", "Roles, Responsibilities, and Authorities", "Policy", "Oversight", "Cybersecurity Supply Chain Risk Management"] },
  { title: "IDENTIFY", className: "bg-identify", key: "Identify", items: ["Assets Management", "Risk Assessment", "Improvement"] },
  { title: "PROTECT", className: "bg-purple", key: "Protect", items: ["Identity Management, Authentication, and Access Control", "Awareness and Training", "Data Security", "Platform Security", "Technology Infrastructure Resilience"] },
  { title: "DETECT", className: "bg-detect", key: "Detect", items: ["Continuous Monitoring", "Adverse Event Analysis"] },
  { title: "RESPOND", className: "bg-respond", key: "Respond", items: ["Incident Management", "Incident Analysis", "Incident Response Reporting and Communication", "Incident Mitigation"] },
  { title: "RECOVER", className: "bg-recover", key: "Recover", items: ["Incident Recovery Plan Execution", "Incident Recovery Communication"] },
];

export const nist1chartData = [
  { title: "Identify (ID)", labels: ["Assets Management (ID.AM)", "Business Environment (ID.BE)", "Governance (ID.GV)", "Risk Assessment (ID.RA)", "Risk Management (ID.RM)", "Supply Chain (ID.SC)"], data: [] },
  { title: "Protect (PR)", labels: ["Access Control (PR.AC)", "Awareness and Training (PR.AT)", "Data Security (PR.DS)", "Information Protection Processes and Procedures (PR.IP)", "Maintenance (PR.MA)", "Protective Technology (PR.PT)"], data: [] },
  { title: "Detect (DE)", labels: ["Anomalies & Events (DE.AE)", "Security Continuous Monitoring (DE.CM)", "Detection Processes (DE.DP)"], data: [] },
  { title: "Respond (RS)", labels: ["Response Planning (RS.RP)", "Communication (RS.CO)", "Analysis (RS.AN)", "Mitigation (RS.MI)", "Response Improvements (RS.IM)"], data: [] },
  { title: "Recover (RC)", labels: ["Recovery Planning (RC.RP)", "Recovery Improvements (RC.IM)", "Communication (RC.CO)"], data: [] },
];

export async function getnist1ChartDataWithScores(API_URL) {
  const response = await fetch(`${API_URL}api/nist1_status/`);
  const nistData = await response.json();
  function getScore(label) {
    const cleanLabel = label.split(' (')[0];
    const record = nistData.find(item => item.subcategory === cleanLabel);
    return record?.score ?? 0;
  }
  return nist2chartData.map((category) => {
    const scores = category.labels.map(getScore);
    const avg = scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2) : "0";
    return { ...category, data: scores, averageScore: avg };
  });
}

export function getNist1CombinedRadarData(dataWithScores) {
  const labels = [];
  const data = [];
  if (!Array.isArray(dataWithScores)) return { labels: [], datasets: [] };
  dataWithScores.forEach((category) => {
    category.labels.forEach((label, index) => {
      labels.push(label);
      data.push(category.data[index] ?? 0);
    });
  });
  return {
    labels,
    datasets: [{
      label: "Progress", data, fill: true, backgroundColor: "rgba(54, 162, 235, 0.2)",
      borderColor: "#36A2EB", pointBackgroundColor: "#36A2EB", pointBorderColor: "#fff",
      pointHoverBackgroundColor: "#fff", pointHoverBorderColor: "#36A2EB", tension: 0.3,
    }],
  };
}

export const sectionsNist1WithHr = ['DETECT', 'IDENTIFY', 'PROTECT', 'RESPOND', 'RECOVER'];

export const sectionDataNist1 = [
  { title: "IDENTIFY", className: "bg-identify", key: "Identify", items: ["Asset Management", "Business Environment", "Governance", "Risk Assessment", "Risk Management", "Supply Chain"] },
  { title: "PROTECT", className: "bg-purple", key: "Protect", items: ["Access Control", "Awareness and Training", "Data Security", "Information Protection Processes and Procedures", "Maintenance", "Protective Technology"] },
  { title: "DETECT", className: "bg-detect", key: "Detect", items: ["Anomalies & Events", "Security Continuous Monitoring", "Detection Processes"] },
  { title: "RESPOND", className: "bg-respond", key: "Respond", items: ["Response Planning", "Communication", "Analysis", "Mitigation", "Response Improvements"] },
  { title: "RECOVER", className: "bg-recover", key: "Recover", items: ["Recovery Planning", "Recovery Improvements", "Communications"] },
];

export const ransomwarechartData = [
  { title: "Identify (ID)", labels: ["Assets Management (ID.AM)", "Business Environment (ID.BE)", "Governance (ID.GV)", "Risk Assessment (ID.RA)", "Risk Management (ID.RM)", "Supply Chain (ID.SC)"], data: [] },
  { title: "Protect (PR)", labels: ["Access Control (PR.AC)", "Awareness & Training (PR.AT)", "Data Security (PR.DS)", "Information Protection Processes and Procedures (PR.IP)", "Maintenance (PR.MA)", "Protective Technology (PR.PT)"], data: [] },
  { title: "Detect (DE)", labels: ["Anomalies & Events (DE.AE)", "Security Continuous Monitoring (DE.CM)", "Detection Processes (DE.DP)"], data: [] },
  { title: "Respond (RS)", labels: ["Response Planning (RS.RP)", "Communications (RS.CO)", "Analysis (RS.AN)", "Mitigation (RS.MI)", "Improvement (RS.IM)"], data: [] },
  { title: "Recover (RC)", labels: ["Recovery Planning (RC.RP)", "Improvements (RC.IM)", "Communication (RC.CO)"], data: [] },
];

export async function getransomwareChartDataWithScores(API_URL) {
  const response = await fetch(`${API_URL}api/ransomware_status/`);
  const nistData = await response.json();
  function getScore(label) {
    const cleanLabel = label.split(' (')[0];
    const record = nistData.find(item => item.subcategory === cleanLabel);
    return record?.score ?? 0;
  }
  return nist2chartData.map((category) => {
    const scores = category.labels.map(getScore);
    const avg = scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2) : "0";
    return { ...category, data: scores, averageScore: avg };
  });
}

export function getRansomwareCombinedRadarData(dataWithScores) {
  const labels = [];
  const data = [];
  if (!Array.isArray(dataWithScores)) return { labels: [], datasets: [] };
  dataWithScores.forEach((category) => {
    category.labels.forEach((label, index) => {
      labels.push(label);
      data.push(category.data[index] ?? 0);
    });
  });
  return {
    labels,
    datasets: [{
      label: "Progress", data, fill: true, backgroundColor: "rgba(54, 162, 235, 0.2)",
      borderColor: "#36A2EB", pointBackgroundColor: "#36A2EB", pointBorderColor: "#fff",
      pointHoverBackgroundColor: "#fff", pointHoverBorderColor: "#36A2EB", tension: 0.3,
    }],
  };
}

export const sectionsRansomwareWithHr = ['DETECT', 'IDENTIFY', 'PROTECT', 'RESPOND', 'RECOVER'];

export const sectionDataRansomware = [
  { title: "IDENTIFY", className: "bg-identify", key: "Identify", items: ["Assets Management", "Business Environment", "Governance", "Risk Assessment", "Risk Management", "Supply Chain"] },
  { title: "PROTECT", className: "bg-purple", key: "Protect", items: ["Access Control", "Awareness and Training", "Data Security", "Information Protection Processes and Procedures", "Maintenance", "Protective Technology"] },
  { title: "DETECT", className: "bg-detect", key: "Detect", items: ["Anomalies & Events", "Security Continuous Monitoring", "Detection Processes"] },
  { title: "RESPOND", className: "bg-respond", key: "Respond", items: ["Response Planning", "Communication", "Analysis", "Mitigation", "Response Improvements"] },
  { title: "RECOVER", className: "bg-recover", key: "Recover", items: ["Recovery Planning", "Recovery Improvements", "Communication"] },
];


// ============================================================================
// 4. API DATA FETCHERS
// ============================================================================
export const loadFrameworkData = async (frameworkKey, forceRefresh = false) => {
  try {
    const data = await secureDataService.loadFrameworkData(frameworkKey, forceRefresh);
    return data;
  } catch (error) {
    console.error(`Error loading framework data for '${frameworkKey}':`, error);
    throw error;
  }
};

export const loadNISTCSF2Data = async (forceRefresh = false) => {
  return loadFrameworkData('nist_csf_2_0', forceRefresh);
};

export const loadNISTCSF1Data = async (forceRefresh = false) => {
  return loadFrameworkData('nist_csf1', forceRefresh);
};

// ============================================================================
// 5. REUSABLE REACT HOOKS
// ============================================================================

/**
 * Universal React Hook to fetch ANY framework data dynamically
 */
export const useFrameworkData = (frameworkKey, options = {}) => {
  const { fetchOnMount = true, useCache = true } = options;

  const [frameworkData, setFrameworkData] = useState(null);
  const [categories, setCategories] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [prefillMap, setPrefillMap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!frameworkKey) return;
    try {
      setLoading(true);
      setError(null);

      const result = await loadFrameworkData(frameworkKey, forceRefresh || !useCache);
      if (result) {
        setFrameworkData(result);
        setCategories(result.categories || result.dataMap || result);
        setMetadata(result.metadata || result._metadata || {});
        setPrefillMap(result.industry_prefill_map || null);
      } else {
        setError(`Failed to load framework data for ${frameworkKey}`);
      }
    } catch (err) {
      setError(err.message || 'Error loading framework data');
    } finally {
      setLoading(false);
    }
  }, [frameworkKey, useCache]);

  const refetch = useCallback(() => fetchData(true), [fetchData]);

  useEffect(() => {
    if (fetchOnMount && frameworkKey) {
      fetchData();
    }
    // Re-fetch whenever the UI language changes mid-session, matching
    // useISO27001Data/useCisV81Data's language-reactive pattern below -
    // without this, switching language after the initial fetch leaves
    // frameworkData/categories showing whatever language was active on
    // mount until something else forces a refetch().
  }, [frameworkKey, fetchOnMount, fetchData, i18n.language]);

  return {
    frameworkData,
    categories,
    metadata,
    prefillMap,
    loading,
    error,
    refetch,
  };
};

/**
 * Dedicated React Hook for NIST CSF 2.0 (Full Backward Compatibility)
 */
export const useNISTCSF2Data = (options = {}) => {
  const { frameworkData, categories, loading, error, refetch } = useFrameworkData('nist_csf_2_0', options);

  return {
    dataMap: categories,
    contentMap: frameworkData?.contentMap || {},
    dataMapData: categories,
    loading,
    error,
    refetch,
    retry: refetch,
    clearCache: () => { },
    retryCount: 0,
  };
};

/**
 * Dedicated React Hook for NIST CSF 1.1 (Full Backward Compatibility)
 */
export const useNISTCSF1Data = (options = {}) => {
  const { frameworkData, categories, loading, error, refetch } = useFrameworkData('nist_csf1', options);

  return {
    dataMap: categories,
    contentMap: frameworkData?.contentMap || {},
    controlMappings: frameworkData?.controlMappings || {},
    dataMapData: categories,
    loading,
    error,
    refetch,
    retry: refetch,
    clearCache: () => { },
    retryCount: 0,
  };
};

/**
 * Dedicated React Hook for NIST Ransomware (Full Backward Compatibility)
 */
export const useRansomwareData = (options = {}) => {
  const { frameworkData, categories, loading, error, refetch } = useFrameworkData('ransomware', options);

  return {
    dataMap: categories,
    contentMap: frameworkData?.contentMap || {},
    controlMappings: frameworkData?.controlMappings || {},
    dataMapData: categories,
    loading,
    error,
    refetch,
    retry: refetch,
    clearCache: () => { },
    retryCount: 0,
  };
};

/**
 * Dedicated React Hook for NIST AI RMF
 */
export const useNistAiRMFData = (options = {}) => {
  const { frameworkData, categories, loading, error, refetch } = useFrameworkData('nist_ai_rmf', options);

  return {
    tabData: categories,
    loading,
    error,
    refetch,
    retry: refetch,
    clearCache: () => { },
    retryCount: 0,
  };
};

/**
 * Dedicated React Hook for ISO 27001:2022
 *
 * ISO 27001's real control catalog (`questionnaire_data/iso27001.json`,
 * shape: {_metadata, IsoRequirements, OrganisationalControls, PeopleControl,
 * PhysicalControl, TechnologicalControl}) is fundamentally different from
 * the `categories`/`dataMap` shape the generic `api/secure/framework-data/
 * <key>/` route's translation overlay understands (see
 * secure_data_loader.py's `_apply_framework_translation_overlay`), so -
 * mirroring `useCisV81Data` below - this hook calls ISO's own dedicated
 * endpoint, `api/get_iso27001_questions_data/` (see
 * cara_backend/api/iso_framework.py), directly instead of routing through
 * `useFrameworkData`/`secureDataService.loadFrameworkData`, passing `?lang=`
 * and refetching whenever the UI language changes so the translation
 * overlay (or English fallback) is re-applied without a full remount.
 */
export const useISO27001Data = () => {
  const [frameworkData, setFrameworkData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${CISV8_API_URL}api/get_iso27001_questions_data/`, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
        params: { lang: i18n.language },
      });

      if (response.data && response.data.status === 'SUCCESS' && response.data.data) {
        setFrameworkData(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to load ISO 27001 data');
      }
    } catch (err) {
      setError(err.message || 'Error loading ISO 27001 data');
    } finally {
      setLoading(false);
    }
  }, []);

  const refetch = useCallback(() => fetchData(), [fetchData]);

  useEffect(() => {
    fetchData();
    // Re-fetch when the UI language changes mid-session, matching
    // useCisV81Data's language-reactive pattern.
  }, [fetchData, i18n.language]);

  return {
    tabData: frameworkData, // Preserves the same shape ViewIsoCsfCara.jsx/AssessmentsDashboard_iso.jsx already destructure as `tabData: isoData`.
    categories: frameworkData,
    loading,
    error,
    refetch,
    retry: refetch,
    clearCache: () => { },
    retryCount: 0,
  };
};

export const useRBICPSData = (options = {}) => {
  const { frameworkData, categories, loading, error, refetch } = useFrameworkData('rbi_cps', options);

  return {
    tabData: categories,
    loading,
    error,
    refetch,
    retry: refetch,
    clearCache: () => { },
    retryCount: 0,
  };
};

// ============================================================================
// 6. HELPER FUNCTIONS
// ============================================================================
export const getAllFrameworks = () => Object.values(FRAMEWORKS_REGISTRY);

export const getFrameworkMetadata = (frameworkKey) => FRAMEWORKS_REGISTRY[frameworkKey] || null;

export const getIndustryPreFill = (frameworkKey, industryName) => {
  if (!industryName) return { priority: 'Medium', status: 'Not Implemented' };
  const mapKey = {
    nist_csf_2_0: industryPreFillMapNist,
    nist_csf1: industryPreFillMapNist,
    iso27001: industryPreFillMapISO,
    ransomware: industryPreFillMapRansomware,
    cis_v8: industryPreFillMapCISv8,
    nist_ai_rmf: industryPreFillMapNistAI,
    rbi_cps: industryPreFillMapRBI,
  }[frameworkKey] || industryPreFillMapNist;

  return mapKey[industryName] || { priority: 'Medium', status: 'Partially Implemented' };
};

export default {
  FRAMEWORKS_REGISTRY,
  industryPreFillMapNist,
  industryPreFillMapNistTarget,
  industryPreFillMapISO,
  industryPreFillMapRansomware,
  industryPreFillMapCISv8,
  industryPreFillMapNistAI,
  industryPreFillMapRBI,
  statusColors,
  getStatusColor,
  getRadarData,
  options,
  loadFrameworkData,
  loadNISTCSF2Data,
  loadNISTCSF1Data,
  useFrameworkData,
  useNISTCSF2Data,
  useNISTCSF1Data,
  useRansomwareData,
  useNistAiRMFData,
  useISO27001Data,
  useRBICPSData,
  getAllFrameworks,
  getFrameworkMetadata,
  getIndustryPreFill,
};

/**
 * Dedicated React Hook for CIS v8.1
 *
 * Unlike the other frameworks (NIST CSF 2.0/1.1, Ransomware, ISO 27001),
 * which are served by the generic `api/secure/framework-data/<key>/` route
 * in secure_data_loader.py, CIS v8.1's real control catalog is served by
 * its own dedicated endpoint, `api/get_cisv8_questions_data/` (see
 * cara_backend/api/cisv8_framework.py), because the data shape
 * (`tabData`/`implementationGroups`, keyed by CIS Control number and
 * Implementation Group) is fundamentally different from the
 * `categories`/`dataMap` shape the generic route's translation overlay
 * understands. This hook calls that dedicated endpoint directly (instead of
 * routing through `useFrameworkData`/`secureDataService.loadFrameworkData`,
 * which would hit the generic route and get untranslated English back for
 * hi/ar), passing `?lang=` and refetching whenever the UI language changes.
 */
export const useCisV81Data = () => {
  const [frameworkData, setFrameworkData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${CISV8_API_URL}api/get_cisv8_questions_data/`, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
        params: { lang: i18n.language },
      });

      if (response.data && response.data.success && response.data.data) {
        setFrameworkData(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to load CIS v8.1 questions data');
      }
    } catch (err) {
      setError(err.message || 'Error loading CIS v8.1 questions data');
    } finally {
      setLoading(false);
    }
  }, []);

  const refetch = useCallback(() => fetchData(), [fetchData]);

  useEffect(() => {
    fetchData();
    // Re-fetch when the UI language changes mid-session so the translation
    // overlay (or fallback to English) is re-applied, matching the
    // language-reactive pattern documented in useSecureData.js.
  }, [fetchData, i18n.language]);

  return {
    frameworkData,
    loading,
    error,
    refetch,
  };
};
